// Shared formatting helpers for the IMS container realm.
// Every view must render numbers/dates/money through these so the whole realm reads the same.

/**
 * The masked-value sentinel (mirrors backend container-acl `MASK`).
 *
 * When a user isn't cleared to see a financial figure or a client/vendor name,
 * the SERVER replaces the value with this string before it ever reaches the
 * browser — the real number is never sent. Every formatter below passes it
 * through verbatim so it renders as `*****` instead of being turned into `—`
 * (which would read like "₹0" or "no data"). Because these formatters are the
 * single choke point for money/number rendering in the realm, one guard per
 * function covers the whole app.
 */
export const MASK = '*****';
export function isMasked(v: unknown): boolean {
  return typeof v === 'string' && v === MASK;
}

/**
 * GLOBAL pricing-hidden switch (belt-and-suspenders on top of the server-side
 * masking). When the signed-in user has `hidePricing`, this is flipped on and
 * EVERY money formatter below returns `*****` regardless of what value it was
 * given — so no ₹ figure can render anywhere in the IMS realm even if a new
 * endpoint forgets to mask a field. The AuthProvider sets it from the user's
 * containerAccess; admins resolve to `false` upstream, so they are unaffected.
 */
let _pricingHidden = false;
export function setPricingHidden(v: boolean): void { _pricingHidden = v === true; }
export function isPricingHidden(): boolean { return _pricingHidden; }
/** True when the money value must render as `*****` — either already masked by
 *  the server, or hidden globally for this user. */
export function moneyHidden(v: unknown): boolean { return _pricingHidden || isMasked(v); }

/**
 * Sum a set of values that MIGHT be masked. If any input is masked, the total is
 * masked too — a user who can't see the individual rates must not see the total
 * derived from them (and it stops `sum + "*****"` producing concatenation
 * garbage). With no masked inputs it behaves like a plain numeric sum.
 */
export function sumMasked(vals: Array<number | string | null | undefined>): number | string {
  for (const v of vals) if (isMasked(v)) return MASK;
  return (vals as Array<number | string | null | undefined>).reduce<number>(
    (s, v) => s + (Number(v) || 0),
    0,
  );
}

export function inr(n: number | string | null | undefined, opts?: { compact?: boolean; dash?: string }): string {
  if (moneyHidden(n)) return MASK;
  const dash = opts?.dash ?? '—';
  if (n == null || !Number.isFinite(n as number)) return dash;
  n = n as number;
  if (opts?.compact) {
    const abs = Math.abs(n);
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
    if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)} K`;
  }
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export function usd(n: number | string | null | undefined, dash = '—'): string {
  if (moneyHidden(n)) return MASK;
  if (n == null || !Number.isFinite(n as number)) return dash;
  return `$${Math.round(n as number).toLocaleString('en-US')}`;
}

export function num(n: number | string | null | undefined, dash = '—'): string {
  if (isMasked(n)) return MASK;
  if (n == null || !Number.isFinite(n as number)) return dash;
  return (n as number).toLocaleString('en-IN');
}

export function pct(n: number | string | null | undefined, digits = 0, dash = '—'): string {
  if (isMasked(n)) return MASK;
  if (n == null || !Number.isFinite(n as number)) return dash;
  return `${(n as number).toFixed(digits)}%`;
}

/** Human date: 12 Jun 2026. Accepts ISO, dd/mm/yyyy, Date. */
export function fmtDate(v: string | number | Date | null | undefined, dash = '—'): string {
  if (isMasked(v)) return MASK;
  const d = toDate(v);
  if (!d) return dash;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function toDate(v: string | number | Date | null | undefined): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(v).trim();
  if (!s) return null;

  /**
   * dd/mm/yyyy — WITH OR WITHOUT a time suffix.
   *
   * The pattern used to be anchored to the end, so it only matched a bare date.
   * Every FMS timestamp carries a time ("30/10/2025 10:06:00"), so those fell
   * through to `new Date()`, which reads American MM/DD. That produced two
   * different wrongs:
   *
   *   "30/10/2025 10:06:00" -> month 30 -> Invalid Date -> rendered as "—",
   *                            which is why so much of the container history
   *                            looked like it had no dates at all;
   *   "05/06/2026 10:00:00" -> read as MAY 6 instead of 5 JUNE — silently
   *                            wrong, and far more dangerous than a dash,
   *                            because nothing looks broken.
   *
   * Every sheet in this system writes day-first, so day-first is what this
   * parses — deliberately, before `new Date()` ever sees the string.
   */
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:[\s,T]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    const yr = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const day = Number(m[1]);
    const mon = Number(m[2]);
    // A month above 12 means the string is month-first after all; trust the
    // numbers over the convention rather than producing a nonsense date.
    const dayFirst = mon <= 12;
    const d = new Date(
      yr,
      (dayFirst ? mon : day) - 1,
      dayFirst ? day : mon,
      Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0),
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function dateMs(v: string | number | Date | null | undefined): number {
  const d = toDate(v);
  return d ? d.getTime() : 0;
}

/** "3 years 2 months" / "18 days" — for ages and idle times. */
export function humanDays(days: number | string | null | undefined, dash = '—'): string {
  if (isMasked(days)) return MASK;
  if (days == null || !Number.isFinite(days as number)) return dash;
  const d = Math.max(0, Math.round(days as number));
  if (d < 45) return `${d} day${d === 1 ? '' : 's'}`;
  if (d < 365) {
    const mo = Math.round(d / 30);
    return `${mo} month${mo === 1 ? '' : 's'}`;
  }
  const yr = Math.floor(d / 365);
  const mo = Math.round((d % 365) / 30);
  return mo ? `${yr}y ${mo}m` : `${yr} year${yr === 1 ? '' : 's'}`;
}

/** Tint strength 0..4 for heat-mapped matrix cells. */
export function heatStep(value: number, max: number): number {
  if (!max || value <= 0) return 0;
  const r = value / max;
  if (r > 0.75) return 4;
  if (r > 0.5) return 3;
  if (r > 0.25) return 2;
  return 1;
}

export function csvEscape(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadCsv(filename: string, headers: string[], rows: Array<Array<unknown>>) {
  const body = [headers.map(csvEscape).join(','), ...rows.map((r) => r.map(csvEscape).join(','))].join('\n');
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
